import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Copy, Send, Link as LinkIcon, Check, Mail, RefreshCw, Trash2, Ticket, Gift } from "lucide-react";

export default function InviteStudentsCard() {
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [waivesPayment, setWaivesPayment] = useState(false);
  const [maxUses, setMaxUses] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copiedToken, setCopiedToken] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/api/mentorship/invitations");
      setInvites(res.data.invitations || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao carregar convites.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/api/mentorship/invitations", {
        email: email || null,
        origin: window.location.origin,
        access_code: accessCode ? accessCode.trim().toUpperCase() : null,
        waives_payment: waivesPayment,
        max_uses: Math.max(1, parseInt(maxUses || 1, 10)),
      });
      setEmail("");
      setAccessCode("");
      setWaivesPayment(false);
      setMaxUses(1);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao criar convite.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async (token) => {
    const url = `${window.location.origin}/convite/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 1500);
    } catch {
      window.prompt("Copie o link:", url);
    }
  };

  return (
    <div
      data-testid="invite-students-card"
      className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-bold text-[#FF6B00] uppercase tracking-wider">
          <Send className="w-3.5 h-3.5" />
          <span>Convidar Aluno</span>
        </div>
        <button
          onClick={load}
          className="p-1 text-gray-400 hover:text-white"
          title="Atualizar"
          data-testid="invites-refresh-btn"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      <form onSubmit={create} className="space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="relative">
            <Mail className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-2.5" />
            <input
              type="email"
              placeholder="e-mail do aluno (opcional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="invite-email-field"
              className="w-full bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 pl-8 pr-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
            />
          </div>
          <div className="relative">
            <Ticket className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-2.5" />
            <input
              type="text"
              placeholder="código curto (opcional)"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
              data-testid="invite-access-code-field"
              className="w-full bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 pl-8 pr-3 text-white text-xs uppercase tracking-widest focus:outline-none focus:border-[#FF6B00]"
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <label className="flex items-center gap-1.5 text-[11px] text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={waivesPayment}
              onChange={(e) => setWaivesPayment(e.target.checked)}
              data-testid="invite-waives-payment-toggle"
              className="accent-[#FF6B00]"
            />
            <Gift className="w-3.5 h-3.5 text-[#FF6B00]" />
            <span>Dispensar pagamento</span>
          </label>
          <label className="flex items-center gap-1 text-[11px] text-gray-300">
            <span>Usos:</span>
            <input
              type="number"
              min={1}
              max={1000}
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              data-testid="invite-max-uses-field"
              className="w-16 bg-[#0F1219] border border-[#282D3D] rounded py-1 px-2 text-white text-xs"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid="create-invite-btn"
            className="px-3 py-1.5 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider disabled:opacity-60"
          >
            {loading ? "Gerando..." : "Gerar convite"}
          </button>
        </div>
      </form>
      {error && (
        <div className="text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2">
          {error}
        </div>
      )}

      {invites.length > 0 && (
        <div className="space-y-1.5 max-h-52 overflow-y-auto">
          {invites.slice(0, 20).map((inv) => (
            <div
              key={inv.id}
              data-testid={`invite-row-${inv.token}`}
              className="flex items-center gap-2 bg-[#0F1219] border border-[#282D3D] rounded-lg p-2 text-[11px]"
            >
              <LinkIcon className="w-3 h-3 text-gray-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-white font-mono truncate">
                  {inv.access_code ? (
                    <span className="text-[#FF6B00] font-extrabold tracking-widest">{inv.access_code}</span>
                  ) : (
                    <span>/convite/{inv.token.slice(0, 10)}…</span>
                  )}
                </div>
                <div className="text-gray-400 text-[10px] flex items-center gap-1.5 flex-wrap">
                  <span>{inv.invited_email ? inv.invited_email : "sem e-mail"}</span>
                  <span>·</span>
                  <span>{inv.status}</span>
                  <span>·</span>
                  <span>{inv.uses_count || 0}/{inv.max_uses || 1} usos</span>
                  {inv.waives_payment && (
                    <span className="text-emerald-400 font-bold">· sem pagamento</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => copy(inv.token)}
                data-testid={`copy-invite-${inv.token}`}
                className="p-1.5 rounded bg-[#1F2330] hover:bg-[#2A3040] text-gray-300"
                title="Copiar link"
              >
                {copiedToken === inv.token ? (
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
